import { StyleSheet } from "react-native";
import type { Palette } from "@jackedlogs/shared";

// Style map for the single-mode (Plan) editor chrome — the meta band, the
// exercise list with its collapsed rows, the inline/expanded editor frame,
// and the wide master-detail panes. The set table itself keeps reading
// makeComposerStyles (ComposerSetTable is shared verbatim with program
// mode); this sheet only dresses the Plan-specific layout around it.
// Tap targets ≥44pt, token-only, dark/light via the resolved palette.
export function makePlanStyles(p: Palette) {
  return StyleSheet.create({
    listContent: { padding: 16, gap: 12, paddingBottom: 64 },

    // Meta band — always visible at the top (name + a Details disclosure
    // for date + notes). One bordered surface, never nested.
    metaBand: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 12,
      padding: 12,
      gap: 10,
    },
    disclosureBtn: {
      minHeight: 44,
      flexDirection: "row",
      alignItems: "center",
      gap: 6,
    },
    disclosureText: {
      color: p.ink3,
      fontSize: 12,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },

    sectionLabel: {
      color: p.ink3,
      fontSize: 13,
      fontWeight: "700",
      letterSpacing: 0.8,
      textTransform: "uppercase",
    },
    listHint: { color: p.ink3, fontSize: 12 },

    // Collapsed exercise row — name + one summary line per member + a ▸
    // affordance. Selected (wide detail pane open) tints brand100 AND shows
    // ▾, never colour alone.
    entryRow: {
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: 10,
      paddingHorizontal: 12,
      paddingVertical: 10,
      minHeight: 44,
      gap: 4,
    },
    entryRowSelected: { backgroundColor: p.brand100, borderColor: p.brand500 },
    entryRowPressed: { backgroundColor: p.surface2 },
    entryHeadLine: { flexDirection: "row", alignItems: "center", gap: 8 },
    entryChevron: { color: p.ink3, fontSize: 16, fontWeight: "700", width: 16, textAlign: "center" },
    supersetTag: {
      color: p.brand500,
      fontSize: 10,
      fontWeight: "800",
      letterSpacing: 0.6,
    },
    memberLine: { flexDirection: "row", alignItems: "center", gap: 8 },
    memberOrdinal: { color: p.brand500, fontSize: 13, fontWeight: "800", width: 22 },
    memberName: { color: p.ink, fontSize: 16, fontWeight: "600", flex: 1 },
    memberSummary: {
      color: p.ink2,
      fontSize: 13,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
    },
    verbatimNote: { color: p.ink3, fontSize: 12 },

    // Expanded / inline editor frame around the reused set table.
    editorFrame: {
      backgroundColor: p.surface,
      borderColor: p.brand500,
      borderWidth: 1,
      borderRadius: 10,
      padding: 12,
      gap: 10,
    },
    editorHead: { flexDirection: "row", alignItems: "center", gap: 8 },
    editorTitle: { color: p.ink, fontSize: 16, fontWeight: "700", flex: 1 },
    collapseBtn: {
      minHeight: 36,
      minWidth: 36,
      alignItems: "center",
      justifyContent: "center",
    },
    collapseText: { color: p.ink3, fontSize: 16, fontWeight: "700" },
    removeBtn: {
      minHeight: 40,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: p.danger,
      alignItems: "center",
      justifyContent: "center",
    },
    removeBtnText: { color: p.danger, fontSize: 14, fontWeight: "700" },

    // Wide (desktop console) master-detail panes — mirrors Maxes/Catalog.
    masterRow: { flex: 1, flexDirection: "row" },
    listPane: {
      width: 360,
      borderRightWidth: 1,
      borderRightColor: p.border,
    },
    detailPane: { flex: 1 },
    detailContent: { padding: 16, gap: 12, paddingBottom: 64 },
    paneHeading: { color: p.ink, fontSize: 20, fontWeight: "700" },
    emptyPane: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24, gap: 8 },
    emptyGlyph: { color: p.ink3, fontSize: 32, fontWeight: "300" },
    emptyTitle: { color: p.ink3, fontSize: 14, textAlign: "center" },
  });
}

export type PlanStyles = ReturnType<typeof makePlanStyles>;
